import DemoBadge from '@/components/feature/DemoBadge';

/**
 * NOTE ON DATA HONESTY
 * --------------------
 * Subscriber figures are NOT read from the live site. Reading `gs_subscribers`
 * requires authenticated admin access (part of the admin build), and exposing
 * that table publicly would leak subscriber emails — so every number here is a
 * clearly labeled SAMPLE, never presented as a live metric.
 */
const MONTHS = [
  { m: 'May', v: 320 },
  { m: 'Jun', v: 410 },
  { m: 'Jul', v: 505 },
  { m: 'Aug', v: 640 },
  { m: 'Sep', v: 780 },
  { m: 'Oct', v: 915 },
];

const COUNTIES = [
  { name: 'Los Angeles', pct: 54 },
  { name: 'Orange', pct: 24 },
  { name: 'Ventura', pct: 13 },
  { name: 'San Diego', pct: 9 },
];

function SampleChip() {
  return (
    <span className="inline-flex items-center gap-1 font-label font-bold uppercase tracking-widest text-[9px] px-2 py-0.5 rounded-sm bg-accent-100 text-accent-900 border border-accent-300 whitespace-nowrap">
      <span className="w-3 h-3 flex items-center justify-center"><i className="ri-flask-line"></i></span>
      Sample
    </span>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-background-50 border border-background-200 rounded-lg p-5">
      <div className="flex items-center justify-between gap-3">
        <p className="font-label uppercase tracking-widest text-[11px] text-foreground-500">{label}</p>
        <SampleChip />
      </div>
      <p className="mt-2 font-heading font-extrabold text-4xl text-foreground-950">{value}</p>
    </div>
  );
}

export default function SubscriberStats() {
  const max = Math.max(...MONTHS.map((x) => x.v));

  return (
    <section>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <h2 className="font-heading font-extrabold uppercase text-3xl text-foreground-950">Newsletter subscribers</h2>
        <DemoBadge label="Sample data — not connected" />
      </div>

      <div className="mb-6 flex items-start gap-3 bg-secondary-50 border border-secondary-200 rounded-md px-4 py-3">
        <span className="w-5 h-5 flex items-center justify-center text-secondary-700 shrink-0 mt-0.5"><i className="ri-information-line"></i></span>
        <p className="text-sm text-secondary-900">
          Live subscriber analytics aren&apos;t wired up yet. Reading subscriber data requires authenticated admin access, which is part of the admin build — and no subscriber data is exposed to the public site. Every figure on this page is a clearly labeled sample.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-8">
        <StatCard label="Total subscribers" value="3,570" />
        <StatCard label="Avg. open rate" value="42%" />
        <StatCard label="New this month" value="+135" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-background-50 border border-background-200 rounded-lg p-5 md:p-6">
          <div className="flex items-center justify-between gap-3 mb-5">
            <p className="font-label uppercase tracking-widest text-xs text-foreground-500">Signups, last 6 months</p>
            <SampleChip />
          </div>
          <div className="flex items-end gap-3 md:gap-5 h-52">
            {MONTHS.map((x) => (
              <div key={x.m} className="flex-1 flex flex-col items-center justify-end gap-2 h-full">
                <span className="font-label text-xs text-foreground-600">{x.v}</span>
                <div className="w-full bg-primary-500 rounded-t-md" style={{ height: `${(x.v / max) * 100}%` }}></div>
                <span className="font-label uppercase tracking-widest text-[11px] text-foreground-500">{x.m}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-background-50 border border-background-200 rounded-lg p-5 md:p-6">
          <div className="flex items-center justify-between gap-3 mb-5">
            <p className="font-label uppercase tracking-widest text-xs text-foreground-500">By county</p>
            <SampleChip />
          </div>
          <ul className="space-y-4">
            {COUNTIES.map((c) => (
              <li key={c.name}>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-foreground-800">{c.name}</span>
                  <span className="font-label font-semibold text-foreground-600">{c.pct}%</span>
                </div>
                <div className="mt-1.5 h-2 rounded-full bg-background-200 overflow-hidden">
                  <div className="h-full bg-accent-500 rounded-full" style={{ width: `${c.pct}%` }}></div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <p className="mt-4 text-xs text-foreground-500 flex items-center gap-1.5">
        <span className="w-4 h-4 flex items-center justify-center"><i className="ri-information-line"></i></span>
        Sample figures shown for the sales demo — once admin access and the newsletter platform are connected, these come from the Backend automatically.
      </p>
    </section>
  );
}